/**
 * Bulk / folder upload planning — pure helpers shared by the admin upload UI
 * (which renames before upload) and the backend (which validates the result).
 *
 * The backend refuses any filename outside UploadFilenameRegex. A folder
 * drop routinely contains names with `#`, `:`, `%`, emoji, or smart quotes,
 * and the same basename in several subfolders (`Q1/report.pdf`,
 * `Q2/report.pdf`) — the registry is keyed on the bare filename, so those
 * would overwrite each other. Planning fixes both before any byte moves, and
 * reports every rename so the admin sees what will land in the registry.
 */
/**
 * Characters allowed in the basename. `\p{L}` keeps diacritics (real
 * filenames carry ë/é/ö); path-traversal and shell metacharacters
 * (`/ \ .. $ \` | ; < >`) stay out by not being in the class.
 */
const ALLOWED_CHAR_CLASS = "[\\p{L}\\p{N}._\\- ()+&,'[\\]]";
export const UPLOAD_FILE_EXTENSIONS = [
    'pdf',
    'odt',
    'docx',
    'doc',
    'pptx',
    'ppt',
    'xlsx',
    'xls',
    'ods',
    'jpg',
    'jpeg',
    'jfif',
    'png',
    'webp',
    'gif',
    'vsdx',
    'vsd',
    'json',
    'csv',
    'md',
];
export const UploadFilenameRegex = new RegExp(`^${ALLOWED_CHAR_CLASS}+\\.(${UPLOAD_FILE_EXTENSIONS.join('|')})$`, 'iu');
export const UploadFilenameAllowedCharRegex = new RegExp(ALLOWED_CHAR_CLASS, 'u');
/** Registry doc ids / Zod cap filenames at 255 UTF-16 units. */
export const MAX_UPLOAD_FILENAME_LENGTH = 255;
const SUPPORTED_EXTENSIONS = new Set(UPLOAD_FILE_EXTENSIONS);
export function uploadFileExtension(name) {
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}
export function isSupportedUploadExtension(name) {
    return SUPPORTED_EXTENSIONS.has(uploadFileExtension(name));
}
function splitExt(name) {
    const i = name.lastIndexOf('.');
    if (i <= 0)
        return { base: name, ext: '' };
    return { base: name.slice(0, i), ext: name.slice(i + 1) };
}
function truncateBase(base, ext) {
    const room = MAX_UPLOAD_FILENAME_LENGTH - (ext ? ext.length + 1 : 0);
    // Iterate by code point so a truncation never splits a surrogate pair.
    let out = '';
    for (const ch of base) {
        if (out.length + ch.length > room)
            break;
        out += ch;
    }
    return out;
}
/**
 * Make `name` acceptable to the backend: NFC-normalise, replace every
 * disallowed character with `_`, collapse runs, trim, cap the length.
 * Returns the input unchanged when it is already valid, so renames are only
 * reported when something actually changed.
 */
export function sanitizeUploadFilename(name) {
    const nfc = name.normalize('NFC');
    const { base, ext } = splitExt(nfc);
    let cleaned = '';
    for (const ch of base) {
        cleaned += UploadFilenameAllowedCharRegex.test(ch) ? ch : '_';
    }
    cleaned = cleaned
        .replace(/_{2,}/g, '_')
        .replace(/\s{2,}/g, ' ')
        // Leading/trailing dots, spaces and underscores are noise after
        // replacement ("#1 draft.pdf" → "_1 draft.pdf" → "1 draft.pdf").
        .replace(/^[\s._]+|[\s._]+$/g, '');
    if (!cleaned)
        cleaned = 'file';
    const truncated = truncateBase(cleaned, ext);
    return ext ? `${truncated}.${ext}` : truncated;
}
/**
 * `name (2).ext`, `name (3).ext`, … — first variant whose lower-cased form
 * is not in `taken` (which must hold lower-cased names).
 */
export function nextFreeUploadName(name, taken) {
    if (!taken.has(name.toLowerCase()))
        return name;
    const { base, ext } = splitExt(name);
    for (let n = 2;; n += 1) {
        const suffix = ` (${n})`;
        const candidateBase = truncateBase(base, ext ? `${ext}${suffix}` : suffix) + suffix;
        const candidate = ext ? `${candidateBase}.${ext}` : candidateBase;
        if (!taken.has(candidate.toLowerCase()))
            return candidate;
    }
}
function parentSegments(relativePath) {
    const parts = relativePath
        .replace(/\\/g, '/')
        .split('/')
        .filter((p) => p.length > 0);
    // Drop the filename itself.
    return parts.slice(0, -1);
}
/**
 * Filter unsupported / empty / oversized files, sanitise names, and make
 * every final name unique within the drop (case-insensitive — Windows and
 * macOS admins treat `Report.pdf` and `report.pdf` as the same file).
 *
 * Same-named files are disambiguated with the closest parent folder name
 * (`Q1 - report.pdf`), widening to more ancestors while that still collides,
 * and finally numbering (`report (2).pdf`) when the paths are identical at
 * every depth (e.g. a file at the root of the drop has no folder to use).
 */
export function planUploadNames(inputs, opts) {
    const skipped = [];
    const kept = [];
    inputs.forEach((f, index) => {
        if (!isSupportedUploadExtension(f.name)) {
            skipped.push({ index, reason: 'unsupported_type' });
        }
        else if (f.size <= 0) {
            skipped.push({ index, reason: 'empty' });
        }
        else if (f.size > opts.maxBytes) {
            skipped.push({ index, reason: 'too_large' });
        }
        else {
            kept.push({
                index,
                sanitized: sanitizeUploadFilename(f.name),
                parents: parentSegments(f.relativePath),
            });
        }
    });
    const names = kept.map((k) => k.sanitized);
    const prefixed = kept.map(() => false);
    const countByKey = () => {
        const m = new Map();
        for (const n of names)
            m.set(n.toLowerCase(), (m.get(n.toLowerCase()) ?? 0) + 1);
        return m;
    };
    const maxDepth = kept.reduce((d, k) => Math.max(d, k.parents.length), 0);
    for (let depth = 1; depth <= maxDepth; depth += 1) {
        const counts = countByKey();
        let changed = false;
        kept.forEach((k, i) => {
            if ((counts.get(names[i].toLowerCase()) ?? 0) < 2)
                return;
            if (k.parents.length === 0)
                return;
            const prefix = k.parents.slice(-Math.min(depth, k.parents.length)).join(' - ');
            const next = sanitizeUploadFilename(`${prefix} - ${k.sanitized}`);
            if (next !== names[i]) {
                names[i] = next;
                prefixed[i] = true;
                changed = true;
            }
        });
        if (!changed)
            break;
    }
    // Whatever still collides (identical paths at every depth, or a root file
    // vs a prefixed one) gets numbered, first occurrence keeps its name.
    const taken = new Set();
    const numbered = kept.map(() => false);
    names.forEach((n, i) => {
        if (taken.has(n.toLowerCase())) {
            names[i] = nextFreeUploadName(n, taken);
            numbered[i] = true;
        }
        taken.add(names[i].toLowerCase());
    });
    const toUpload = kept.map((k, i) => {
        const reasons = [];
        if (k.sanitized !== inputs[k.index].name)
            reasons.push('sanitised');
        if (prefixed[i])
            reasons.push('subfolder_prefix');
        if (numbered[i])
            reasons.push('numbered');
        return { index: k.index, finalName: names[i], renameReasons: reasons };
    });
    return { toUpload, skipped };
}
//# sourceMappingURL=upload-planning.js.map